'use client';

import {
	useQuery,
	UseQueryOptions,
	UseQueryResult,
	QueryKey,
} from '@tanstack/react-query';
import { ServerActionResult } from '@/shared/types/server-action.type';
import { useGlobalDialogStore } from '@/shared/stores/useGlobalDialogStore';
import { errorRegistry } from '@/core/error';
import { withAuth } from '../utils/withAuth';

// Custom error class to carry the error code
export class SafeQueryError extends Error {
	constructor(
		message: string,
		public readonly code: string
	) {
		super(message);
		this.name = 'SafeQueryError';
	}
}

function isGlobalError(error: Error | null): boolean {
	return (
		error instanceof SafeQueryError &&
		errorRegistry[error.code] !== undefined
	);
}

interface UseSafeQueryOptions<TData> extends Omit<
	UseQueryOptions<TData, Error, TData, QueryKey>,
	'queryFn'
> {
	action: () => Promise<ServerActionResult<TData>>;
}

export function useSafeQuery<TData>(
	options: UseSafeQueryOptions<TData>
): UseQueryResult<TData, Error> {
	const { action, ...queryOptions } = options;
	const openDialog = useGlobalDialogStore((state) => state.openDialog);

	const query = useQuery({
		...queryOptions,
		queryFn: async () => {
			const result = await withAuth(action)();

			if (!result.success) {
				const errorCode = result.error?.code ?? 'GENERIC_ERROR';
				const errorMessage =
					result.error?.message ??
					'Something went wrong! Please try again.';

				const dialogConfig = errorRegistry[errorCode];
				if (dialogConfig) {
					openDialog(dialogConfig);
				}
				throw new SafeQueryError(errorMessage, errorCode);
			}

			return result.data as TData;
		},
	});

	// Filter out global errors - component cannot see them
	const globalError = isGlobalError(query.error);
	return {
		...query,
		error: globalError ? null : query.error,
		isError: globalError ? false : query.isError,
	} as UseQueryResult<TData, Error>;
}
